import { Avatar, DataTable, StatusDot, StatusPill } from "@/components/iris";
import type { DataTableColumn } from "@/components/iris";
import type { UserFilters } from "@/hooks/use-users";
import type { UserRecord } from "@/lib/types";

const TYPE_VARIANT: Record<string, "idle" | "ok" | "warn"> = {
  user: "idle",
  admin: "ok",
  superadmin: "warn",
};

const TYPE_LABEL: Record<string, string> = {
  user: "User",
  admin: "Admin",
  superadmin: "Superadmin",
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

interface UsersTableProps {
  users: UserRecord[];
  filters: UserFilters;
  onFiltersChange: (fn: (prev: UserFilters) => UserFilters) => void;
  onRowClick: (id: string) => void;
}

export function UsersTable({ users, filters, onFiltersChange, onRowClick }: UsersTableProps) {
  const handleSort = (columnKey: string) => {
    onFiltersChange((f) => {
      if (f.sort === columnKey && f.order === "asc") {
        return { ...f, sort: columnKey, order: "desc" };
      }
      return { ...f, sort: columnKey, order: "asc" };
    });
  };

  const columns: DataTableColumn<UserRecord>[] = [
    {
      key: "avatar",
      label: "",
      render: (user) =>
        user.profile_image_url ? (
          <img
            src={user.profile_image_url}
            alt=""
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              objectFit: "cover",
            }}
          />
        ) : (
          <Avatar name={user.username || user.email || "?"} size={28} />
        ),
    },
    {
      key: "username",
      label: "Name",
      sortable: true,
      render: (user) => (
        <span style={{ color: "var(--iris-fg-1)", fontWeight: 500 }}>{user.username}</span>
      ),
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      cellVariant: "muted",
    },
    {
      key: "user_type",
      label: "Type",
      sortable: true,
      render: (user) => (
        <StatusPill variant={TYPE_VARIANT[user.user_type] ?? "idle"}>
          {TYPE_LABEL[user.user_type] ?? user.user_type}
        </StatusPill>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (user) => <StatusDot variant={user.status === "active" ? "ok" : "idle"} />,
    },
    {
      key: "user_created_at",
      label: "Created",
      sortable: true,
      cellVariant: "muted",
      render: (user) =>
        user.user_created_at
          ? new Date(user.user_created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "\u2014",
    },
    {
      key: "last_login_at",
      label: "Last Login",
      sortable: true,
      cellVariant: "muted",
      render: (user) => (user.last_login_at ? relativeTime(user.last_login_at) : "Never"),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={users}
      rowKey={(user) => user.id}
      sortKey={filters.sort ?? null}
      sortDirection={filters.order === "asc" ? "asc" : filters.order === "desc" ? "desc" : null}
      onSort={(colKey) => handleSort(colKey)}
      onRowClick={(user) => onRowClick(user.id)}
      emptyMessage="No users found"
    />
  );
}
